/**
 * Curriculum Planner — Program Editor
 *
 * The operational command center for one academic program.
 * Contains:
 *   · Program metadata editor
 *   · Curriculum Roadmap (course table + reorder)
 *   · Program Progress dashboard (per-course status, indicators, readiness)
 *   · Bulk Import (CSV / paste-as-text)
 *   · Prerequisite dependency warnings
 *   · "Build in KAPE" (or "Open in KAPE" once linked)
 *
 * Route: /kcis/admin/university/curriculum-planner/programs/:id
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import KcisAdminShell, { KCIS_ADMIN_PALETTE as PAL } from "@/pages/kcis_admin/KcisAdminShell";
import {
  curriculumApi, KAPE_STATUS_LABEL, PLANNER_STATUS_COLOR, PLANNER_STATUS_LABEL,
} from "@/lib/curriculumApi";

const CARD = { background: PAL.panel, border: `1px solid ${PAL.border}`, borderRadius: 14 };

const INDICATOR_LABELS = {
  blueprint_complete: "Blueprint",
  ai_generation_complete: "AI Gen",
  faculty_review_complete: "Faculty",
  quality_review_passed: "Quality",
  student_preview_completed: "Preview",
  published: "Published",
};

export default function ProgramEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [progress, setProgress] = useState(null);
  const [warnings, setWarnings] = useState({ warnings: [], count: 0 });
  const [enums, setEnums] = useState(null);
  const [tab, setTab] = useState("roadmap"); // roadmap | progress | competencies | bulk
  const [buildModal, setBuildModal] = useState(null); // planned_course id | null

  const load = useCallback(async () => {
    try {
      const [p, pr, w, e] = await Promise.all([
        curriculumApi.getProgram(id),
        curriculumApi.programProgress(id),
        curriculumApi.dependencyWarnings(id),
        curriculumApi.enums(),
      ]);
      setProgram(p.program);
      setProgress(pr);
      setWarnings(w);
      setEnums(e);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load program");
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!program || !progress) {
    return <KcisAdminShell><div className="p-6 text-sm" style={{ color: PAL.muted }}>Loading…</div></KcisAdminShell>;
  }

  return (
    <KcisAdminShell>
      <div className="p-6 space-y-5"
           style={{ background: PAL.bg, color: PAL.text, minHeight: "100vh" }}
           data-testid="program-editor-page">
        <ProgramHeader program={program} progress={progress} onReload={load} />

        {/* Tabs */}
        <div className="flex gap-2 border-b" style={{ borderColor: PAL.border }} data-testid="editor-tabs">
          {[
            ["roadmap", "Curriculum Roadmap"],
            ["progress", "Program Progress"],
            ["competencies", "Competency Matrix"],
            ["bulk", "Bulk Import"],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
                    data-testid={`tab-${k}`}
                    className="px-3 py-2 text-sm"
                    style={{
                      borderBottom: tab === k ? `2px solid ${PAL.accent}` : "2px solid transparent",
                      color: tab === k ? PAL.text : PAL.muted,
                    }}>{l}</button>
          ))}
        </div>

        {tab === "roadmap" && (
          <Roadmap program={program} progress={progress} warnings={warnings}
                    onOpenBuild={setBuildModal} onReload={load} enums={enums} />
        )}
        {tab === "progress" && <ProgressPanel progress={progress} />}
        {tab === "competencies" && <CompetenciesPanel programId={id} />}
        {tab === "bulk" && <BulkImportPanel programId={id} onDone={load} />}
      </div>

      {buildModal && (
        <BuildInKapeModal plannedCourse={buildModal}
                           onClose={() => setBuildModal(null)}
                           onSuccess={() => { setBuildModal(null); load(); }} />
      )}
    </KcisAdminShell>
  );
}

// ═══════════════════════════════════════════════════════
// Header + launch readiness
// ═══════════════════════════════════════════════════════

function ProgramHeader({ program, progress }) {
  const readiness = progress.launch_readiness_score || 0;
  return (
    <div style={{ ...CARD, padding: 20 }} data-testid="program-header">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
            {[program.college, program.school, program.department].filter(Boolean).join(" · ")}
          </div>
          <h1 className="text-2xl font-semibold mt-1" data-testid="program-name">{program.name}</h1>
          <div className="text-sm mt-1" style={{ color: PAL.muted }}>
            {program.credential} · {program.summary || program.description}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-widest" style={{ color: PAL.muted }}>
            Launch readiness
          </div>
          <div className="text-4xl font-semibold" style={{ color: PAL.accent }} data-testid="launch-readiness-score">
            {readiness}%
          </div>
          <div className="text-xs mt-1" style={{ color: PAL.muted }}>
            {progress.stage_totals?.active || 0} of {progress.required_courses_count || progress.total_courses || 0} published
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {["planned", "in_development", "ready", "active", "retired"].map((k) => (
          <div key={k} className="rounded-lg p-3" style={{ background: "#211A2B", border: `1px solid ${PAL.border}` }}
               data-testid={`stage-${k}`}>
            <div className="text-[11px] uppercase" style={{ color: PLANNER_STATUS_COLOR[k] }}>
              {PLANNER_STATUS_LABEL[k]}
            </div>
            <div className="text-2xl font-semibold mt-1">{progress.stage_totals?.[k] || 0}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Roadmap tab
// ═══════════════════════════════════════════════════════

function Roadmap({ program, progress, warnings, onOpenBuild, onReload, enums }) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    title: "", course_code: "", planned_clock_hours: 10,
    credential_level: "Certification", description: "",
  });
  const [saving, setSaving] = useState(false);
  const courses = progress.courses || [];

  const createCourse = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      await curriculumApi.createPlannedCourse({
        program_id: program.id, ...form,
        planned_clock_hours: Number(form.planned_clock_hours) || 10,
      });
      toast.success("Course added");
      setForm({ title: "", course_code: "", planned_clock_hours: 10,
                 credential_level: "Certification", description: "" });
      setShowNew(false);
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const updatePlannerStatus = async (pcId, newStatus) => {
    try {
      await curriculumApi.patchPlannedCourse(pcId, { planner_status: newStatus });
      toast.success("Status updated");
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const openInKape = (kapeId) => {
    window.open(`/kcis/admin/university/course-builder/${kapeId}`, "_blank");
  };

  return (
    <div className="space-y-4">
      {warnings.count > 0 && (
        <div style={{ ...CARD, padding: 12, borderColor: "#F59E0B" }} data-testid="dependency-warnings">
          <div className="text-sm font-medium" style={{ color: "#F59E0B" }}>
            {warnings.count} prerequisite warning{warnings.count === 1 ? "" : "s"}
          </div>
          <ul className="mt-1 text-xs space-y-1" style={{ color: PAL.muted }}>
            {warnings.warnings.slice(0, 5).map((w, i) => (
              <li key={i}>· {w.course_title ? `${w.course_title}: ` : ""}{w.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div className="text-sm" style={{ color: PAL.muted }}>{courses.length} course{courses.length === 1 ? "" : "s"}</div>
        <button onClick={() => setShowNew((s) => !s)}
                className="px-3 py-1.5 rounded-lg text-sm"
                style={{ background: PAL.accent, color: "#12091E" }}
                data-testid="add-course-btn">
          {showNew ? "Cancel" : "+ Add course"}
        </button>
      </div>

      {showNew && (
        <div style={{ ...CARD, padding: 16 }} data-testid="new-course-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SmallField label="Title" value={form.title} onChange={(v) => setForm(f => ({ ...f, title: v }))} testid="new-course-title" />
            <SmallField label="Course code" value={form.course_code} onChange={(v) => setForm(f => ({ ...f, course_code: v }))} testid="new-course-code" />
            <SmallSelect label="Credential" value={form.credential_level}
                          options={(enums?.credential_levels) || ["Certification"]}
                          onChange={(v) => setForm(f => ({ ...f, credential_level: v }))}
                          testid="new-course-credential" />
            <SmallField label="Planned clock hours" type="number" value={form.planned_clock_hours}
                        onChange={(v) => setForm(f => ({ ...f, planned_clock_hours: v }))}
                        testid="new-course-hours" />
            <SmallField label="Description" value={form.description}
                        onChange={(v) => setForm(f => ({ ...f, description: v }))}
                        className="md:col-span-2" testid="new-course-desc" />
          </div>
          <div className="mt-3">
            <button onClick={createCourse} disabled={saving}
                    className="px-3 py-1.5 rounded-lg text-sm disabled:opacity-50"
                    style={{ background: PAL.accent, color: "#12091E" }}
                    data-testid="save-new-course-btn">
              {saving ? "Saving…" : "Add to roadmap"}
            </button>
          </div>
        </div>
      )}

      <div style={{ ...CARD, padding: 0, overflow: "hidden" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "#211A2B", color: PAL.muted }}>
              <th className="text-left px-3 py-2 w-10">#</th>
              <th className="text-left px-3 py-2">Course</th>
              <th className="text-right px-3 py-2">Hours</th>
              <th className="text-left px-3 py-2">Planner status</th>
              <th className="text-left px-3 py-2">KAPE status</th>
              <th className="text-left px-3 py-2 w-52">Indicators</th>
              <th className="text-right px-3 py-2 w-32">Readiness</th>
              <th className="px-3 py-2 w-40 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {courses.length === 0 && (
              <tr><td colSpan={8} className="text-center px-4 py-6" style={{ color: PAL.muted }}
                       data-testid="roadmap-empty">
                No courses yet. Add one manually or use Bulk Import to paste your full curriculum.
              </td></tr>
            )}
            {courses.map((c) => (
              <tr key={c.id} className="border-t" style={{ borderColor: PAL.border }}
                  data-testid={`roadmap-row-${c.id}`}>
                <td className="px-3 py-3 text-center" style={{ color: PAL.muted }}>{c.order}</td>
                <td className="px-3 py-3">
                  <div className="font-medium">{c.title}</div>
                  <div className="text-xs" style={{ color: PAL.muted }}>{c.course_code}</div>
                </td>
                <td className="px-3 py-3 text-right">{c.planned_clock_hours}</td>
                <td className="px-3 py-3">
                  <select value={c.planner_status}
                          onChange={(e) => updatePlannerStatus(c.id, e.target.value)}
                          data-testid={`planner-status-${c.id}`}
                          className="text-xs rounded-md px-2 py-1"
                          style={{ background: PAL.bg, border: `1px solid ${PAL.border}`,
                                    color: PLANNER_STATUS_COLOR[c.planner_status] || PAL.text }}>
                    {["planned", "in_development", "ready", "active", "retired"].map((s) => (
                      <option key={s} value={s} style={{ color: PAL.text, background: PAL.bg }}>
                        {PLANNER_STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-3 text-xs" style={{ color: PAL.muted }}>
                  {KAPE_STATUS_LABEL[c.kape_build_status] || "—"}
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(c.indicators || {}).map(([k, v]) => (
                      <span key={k}
                            title={INDICATOR_LABELS[k]}
                            data-testid={`indicator-${c.id}-${k}`}
                            className="text-[10px] px-1.5 py-0.5 rounded"
                            style={{
                              background: v ? "#10B98122" : "#4B556322",
                              color: v ? "#34D399" : PAL.muted,
                              border: `1px solid ${v ? "#10B98155" : PAL.border}`,
                            }}>
                        {INDICATOR_LABELS[k]}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-3 text-right">
                  <span className="text-xs font-medium" style={{ color: PAL.accent }}>
                    {c.launch_readiness_score}%
                  </span>
                </td>
                <td className="px-3 py-3 text-right">
                  {c.kape_course_id ? (
                    <button onClick={() => openInKape(c.kape_course_id)}
                            data-testid={`open-in-kape-${c.id}`}
                            className="px-2 py-1 rounded-md text-xs"
                            style={{ background: PAL.accentSoft, color: PAL.text }}>
                      Open in KAPE →
                    </button>
                  ) : (
                    <button onClick={() => onOpenBuild(c)}
                            data-testid={`build-in-kape-${c.id}`}
                            className="px-2 py-1 rounded-md text-xs font-medium"
                            style={{ background: PAL.accent, color: "#12091E" }}>
                      Build in KAPE
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Progress panel — indicator summaries + charts
// ═══════════════════════════════════════════════════════

function ProgressPanel({ progress }) {
  const t = progress.indicator_totals || {};
  const total = progress.total_courses || 0;
  return (
    <div className="space-y-4" data-testid="progress-panel">
      <div className="text-sm" style={{ color: PAL.muted }}>
        Six-stage production pipeline · {total} course{total === 1 ? "" : "s"} in this program
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(INDICATOR_LABELS).map(([k, l]) => {
          const done = t[k] || 0;
          const pct = total ? Math.round((done / total) * 100) : 0;
          return (
            <div key={k} style={{ ...CARD, padding: 14 }} data-testid={`prog-indicator-${k}`}>
              <div className="flex items-baseline justify-between">
                <div className="text-sm font-medium">{l}</div>
                <div className="text-xs" style={{ color: PAL.muted }}>{done} / {total}</div>
              </div>
              <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: "#211A2B" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: pct === 100 ? "#10B981" : PAL.accent }} />
              </div>
              <div className="mt-1 text-xs" style={{ color: PAL.muted }}>{pct}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Competencies panel
// ═══════════════════════════════════════════════════════

function CompetenciesPanel({ programId }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", category: "clinical", description: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await curriculumApi.listCompetencies({ program_id: programId });
      setItems(d.items || []);
    } catch { /* ignore */ }
  }, [programId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await curriculumApi.createCompetency({ ...form, program_id: programId });
      setForm({ name: "", category: "clinical", description: "" });
      toast.success("Competency added");
      await load();
    } catch (e) { toast.error("Add failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete competency?")) return;
    await curriculumApi.deleteCompetency(id);
    await load();
  };

  return (
    <div className="space-y-4" data-testid="competencies-panel">
      <div style={{ ...CARD, padding: 16 }}>
        <div className="text-sm font-medium mb-2">Add competency</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <SmallField label="Name" value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} testid="new-comp-name" />
          <SmallField label="Category" value={form.category} onChange={(v) => setForm(f => ({ ...f, category: v }))} testid="new-comp-cat" />
          <SmallField label="Description" value={form.description} onChange={(v) => setForm(f => ({ ...f, description: v }))}
                       className="md:col-span-2" testid="new-comp-desc" />
        </div>
        <div className="mt-3">
          <button onClick={add} disabled={saving}
                  className="px-3 py-1.5 rounded-lg text-sm disabled:opacity-50"
                  style={{ background: PAL.accent, color: "#12091E" }}
                  data-testid="save-new-comp-btn">Add</button>
        </div>
      </div>
      <div style={{ ...CARD, padding: 0, overflow: "hidden" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "#211A2B", color: PAL.muted }}>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="px-3 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={4} className="text-center px-4 py-6" style={{ color: PAL.muted }}
                       data-testid="competencies-empty">
                No competencies defined for this program yet.
              </td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-t" style={{ borderColor: PAL.border }} data-testid={`comp-row-${c.id}`}>
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2">{c.category}</td>
                <td className="px-3 py-2" style={{ color: PAL.muted }}>{c.description}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => remove(c.id)}
                          className="text-xs" style={{ color: "#F87171" }}
                          data-testid={`del-comp-${c.id}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Bulk import panel
// ═══════════════════════════════════════════════════════

function BulkImportPanel({ programId, onDone }) {
  const [text, setText] = useState("");
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [busy, setBusy] = useState(false);

  const parse = useCallback((raw) => {
    const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const rows = [];
    for (const line of lines) {
      const cols = line.includes("\t") ? line.split("\t") : line.split(",");
      const [title, code, hours, credential, delivery, description] = cols.map(c => (c || "").trim());
      if (!title || title.toLowerCase() === "title") continue;
      rows.push({
        title,
        course_code: code || "",
        planned_clock_hours: Number(hours) || 10,
        credential_level: credential || "Certification",
        delivery_format: delivery || "self-paced online",
        description: description || "",
      });
    }
    return rows;
  }, []);

  const preview = useMemo(() => parse(text), [text, parse]);

  const submit = async () => {
    if (preview.length === 0) { toast.error("Nothing to import"); return; }
    setBusy(true);
    try {
      const res = await curriculumApi.bulkImport({
        program_id: programId, rows: preview, replace_existing: replaceExisting,
      });
      toast.success(`Imported ${res.inserted} course${res.inserted === 1 ? "" : "s"}`);
      setText("");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="bulk-import-panel">
      <div style={{ ...CARD, padding: 16 }}>
        <div className="text-sm font-medium">Paste courses (one per line)</div>
        <div className="text-xs mt-1" style={{ color: PAL.muted }}>
          Format: <code>Title, Course Code, Hours, Credential, Delivery, Description</code>.<br />
          Only <b>Title</b> is required. Tabs (from spreadsheet paste) also work.
        </div>
        <textarea rows={12} value={text} onChange={(e) => setText(e.target.value)}
                   placeholder="Foundations of Trichology, TRICH-101, 15, Certification, self-paced online, Intro course"
                   data-testid="bulk-import-textarea"
                   className="w-full mt-3 px-3 py-2 rounded-md text-sm font-mono"
                   style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }} />
        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          <label className="text-xs flex items-center gap-2" style={{ color: PAL.muted }}>
            <input type="checkbox" checked={replaceExisting} onChange={(e) => setReplaceExisting(e.target.checked)}
                    data-testid="replace-existing-checkbox" />
            Replace existing courses (retire linked, delete unlinked)
          </label>
          <button onClick={submit} disabled={busy || preview.length === 0}
                  className="px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                  style={{ background: PAL.accent, color: "#12091E" }}
                  data-testid="bulk-import-submit-btn">
            {busy ? "Importing…" : `Import ${preview.length} course${preview.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
      {preview.length > 0 && (
        <div style={{ ...CARD, padding: 0, overflow: "hidden" }}>
          <div className="px-3 py-2 text-xs" style={{ background: "#211A2B", color: PAL.muted }}>
            Preview · {preview.length} row{preview.length === 1 ? "" : "s"}
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ color: PAL.muted }}>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2">Code</th>
                <th className="text-right px-3 py-2">Hours</th>
                <th className="text-left px-3 py-2">Credential</th>
              </tr>
            </thead>
            <tbody>
              {preview.slice(0, 30).map((r, i) => (
                <tr key={i} className="border-t" style={{ borderColor: PAL.border }}
                    data-testid={`bulk-preview-row-${i}`}>
                  <td className="px-3 py-1.5">{r.title}</td>
                  <td className="px-3 py-1.5" style={{ color: PAL.muted }}>{r.course_code}</td>
                  <td className="px-3 py-1.5 text-right">{r.planned_clock_hours}</td>
                  <td className="px-3 py-1.5" style={{ color: PAL.muted }}>{r.credential_level}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Build in KAPE modal
// ═══════════════════════════════════════════════════════

function BuildInKapeModal({ plannedCourse, onClose, onSuccess }) {
  const [form, setForm] = useState({
    audience: "",
    transformation_starting_point: "",
    transformation_desired_endpoint: "",
    course_outcomes: ["", "", ""],
    optional_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.transformation_starting_point.trim() || !form.transformation_desired_endpoint.trim()) {
      toast.error("Transformation starting point and endpoint are required");
      return;
    }
    const outcomes = form.course_outcomes.filter(o => (o || "").trim());
    if (outcomes.length === 0) {
      toast.error("At least one course outcome required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await curriculumApi.buildInKape({
        planned_course_id: plannedCourse.id,
        transformation_starting_point: form.transformation_starting_point,
        transformation_desired_endpoint: form.transformation_desired_endpoint,
        course_outcomes: outcomes,
        audience: form.audience,
        optional_notes: form.optional_notes,
      });
      toast.success("KAPE course created · opening editor…");
      onSuccess();
      window.open(`/kcis/admin/university/course-builder/${res.kape_course_id}`, "_blank");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "already_linked") {
        toast.error("Already linked · opening existing KAPE course");
        window.open(`/kcis/admin/university/course-builder/${detail.kape_course_id}`, "_blank");
        onSuccess();
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Build failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,0.6)" }}
         data-testid="build-in-kape-modal">
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden"
           style={{ background: PAL.panel, border: `1px solid ${PAL.border}`, color: PAL.text }}>
        <div className="px-5 py-4 border-b" style={{ borderColor: PAL.border }}>
          <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
            Build in KAPE · Course Development Blueprint
          </div>
          <div className="text-lg font-semibold mt-1" data-testid="build-modal-title">{plannedCourse.title}</div>
          <div className="text-xs mt-1" style={{ color: PAL.muted }}>
            {plannedCourse.planned_clock_hours} hours · seeded from the Curriculum Planner
          </div>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <SmallField label="Audience" value={form.audience}
                       onChange={(v) => setForm(f => ({ ...f, audience: v }))}
                       placeholder="e.g. Aspiring clinical trichologists"
                       testid="build-audience" />
          <SmallField label="Transformation · Starting point *"
                       value={form.transformation_starting_point}
                       onChange={(v) => setForm(f => ({ ...f, transformation_starting_point: v }))}
                       placeholder="Who is the learner today?"
                       area
                       testid="build-starting" />
          <SmallField label="Transformation · Desired endpoint *"
                       value={form.transformation_desired_endpoint}
                       onChange={(v) => setForm(f => ({ ...f, transformation_desired_endpoint: v }))}
                       placeholder="Who do they become after this course?"
                       area
                       testid="build-endpoint" />
          <div>
            <div className="text-xs mb-1" style={{ color: PAL.muted }}>Course outcomes * (at least one)</div>
            {form.course_outcomes.map((o, i) => (
              <input key={i} value={o}
                      onChange={(e) => {
                        const arr = [...form.course_outcomes];
                        arr[i] = e.target.value;
                        setForm(f => ({ ...f, course_outcomes: arr }));
                      }}
                      data-testid={`build-outcome-${i}`}
                      placeholder={`Outcome ${i + 1}`}
                      className="w-full mb-2 px-2 py-1 rounded-md text-sm"
                      style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }} />
            ))}
            <button onClick={() => setForm(f => ({ ...f, course_outcomes: [...f.course_outcomes, ""] }))}
                    className="text-xs" style={{ color: PAL.accent }}
                    data-testid="add-outcome-btn">+ Add outcome</button>
          </div>
          <SmallField label="Optional notes"
                       value={form.optional_notes}
                       onChange={(v) => setForm(f => ({ ...f, optional_notes: v }))}
                       area
                       testid="build-notes" />
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2"
             style={{ borderColor: PAL.border }}>
          <button onClick={onClose}
                  className="px-3 py-2 rounded-lg text-sm"
                  style={{ background: PAL.accentSoft, color: PAL.text }}
                  data-testid="build-cancel-btn">Cancel</button>
          <button onClick={submit} disabled={submitting}
                  className="px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                  style={{ background: PAL.accent, color: "#12091E" }}
                  data-testid="build-submit-btn">
            {submitting ? "Building…" : "Build in KAPE"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Small form helpers
// ═══════════════════════════════════════════════════════

function SmallField({ label, value, onChange, type = "text", area, testid, className = "", placeholder = "" }) {
  return (
    <label className={`text-xs block ${className}`} style={{ color: PAL.muted }}>
      <div className="mb-1">{label}</div>
      {area ? (
        <textarea rows={3} value={value} placeholder={placeholder}
                   onChange={(e) => onChange(e.target.value)}
                   data-testid={testid}
                   className="w-full px-2 py-1 rounded-md text-sm"
                   style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }} />
      ) : (
        <input type={type} value={value} placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full px-2 py-1 rounded-md text-sm"
                style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }} />
      )}
    </label>
  );
}

function SmallSelect({ label, value, options, onChange, testid, className = "" }) {
  return (
    <label className={`text-xs block ${className}`} style={{ color: PAL.muted }}>
      <div className="mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              data-testid={testid}
              className="w-full px-2 py-1 rounded-md text-sm"
              style={{ background: PAL.bg, border: `1px solid ${PAL.border}`, color: PAL.text }}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
